import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Loader2, Plus, Pencil, Trash2, FolderTree, RotateCcw, Lock } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

const API_URL = window.location.origin;

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    delivery_day: 'Monday',
    meal_type: 'Chilled/Hybrid',
    rotation_type: 'Fixed',
    sort_order: 0,
    is_active: true
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/categories/all`, { withCredentials: true });
      setCategories(data);
    } catch (e) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        delivery_day: category.delivery_day,
        meal_type: category.meal_type,
        rotation_type: category.rotation_type || 'Fixed',
        sort_order: category.sort_order || 0,
        is_active: category.is_active
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '', description: '', delivery_day: 'Monday', meal_type: 'Chilled/Hybrid',
        rotation_type: 'Fixed', sort_order: categories.length + 1, is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingCategory) {
        await axios.put(`${API_URL}/api/categories/${editingCategory.id}`, formData, { withCredentials: true });
        toast.success('Category updated');
      } else {
        await axios.post(`${API_URL}/api/categories`, formData, { withCredentials: true });
        toast.success('Category created');
      }
      setDialogOpen(false);
      fetchCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Delete this category? Products will need reassignment.')) return;
    try {
      await axios.delete(`${API_URL}/api/categories/${categoryId}`, { withCredentials: true });
      toast.success('Category deleted');
      fetchCategories();
    } catch (e) {
      toast.error('Failed to delete category');
    }
  };

  const getMealTypeBadge = (mealType) => {
    const colors = { 'Chilled/Hybrid': 'bg-blue-100 text-blue-700', 'HTE Fixed': 'bg-orange-100 text-orange-700', 'HTE Special': 'bg-purple-100 text-purple-700' };
    return colors[mealType] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (<AdminLayout><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div></AdminLayout>);
  }

  return (
    <AdminLayout>
      <div className="animate-fade-in space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Categories</h1>
            <p className="text-slate-500 mt-1 text-sm">Manage menu categories — Fixed or Rotating (3-week cycle)</p>
          </div>
          <Button onClick={() => handleOpenDialog()} className="rounded-full bg-green-600 hover:bg-green-700" data-testid="add-category-button">
            <Plus className="w-4 h-4 mr-2" />Add Category
          </Button>
        </div>

        <Card className="border-slate-200/60 shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Order</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Day</TableHead>
                    <TableHead>Meal Type</TableHead>
                    <TableHead>Rotation</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        <FolderTree className="w-12 h-12 text-slate-300 mx-auto mb-4" />No categories found
                      </TableCell>
                    </TableRow>
                  ) : (
                    categories.map((cat) => (
                      <TableRow key={cat.id} data-testid={`category-row-${cat.id}`}>
                        <TableCell className="font-medium text-sm">{cat.sort_order}</TableCell>
                        <TableCell>
                          <p className="font-medium text-sm">{cat.name}</p>
                          {cat.description && <p className="text-xs text-slate-500 line-clamp-1">{cat.description}</p>}
                        </TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{cat.delivery_day}</Badge></TableCell>
                        <TableCell><Badge className={`${getMealTypeBadge(cat.meal_type)} text-xs`}>{cat.meal_type}</Badge></TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${cat.rotation_type === 'Rotating' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                            {cat.rotation_type === 'Rotating' ? <><RotateCcw className="w-3 h-3 mr-1 inline" />Rotating</> : <><Lock className="w-3 h-3 mr-1 inline" />Fixed</>}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${cat.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                            {cat.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(cat)} className="h-8 w-8" data-testid={`edit-category-${cat.id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(cat.id)} className="h-8 w-8 text-red-600 hover:bg-red-50" data-testid={`delete-category-${cat.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{editingCategory ? 'Edit Category' : 'Add New Category'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Category Name *</Label>
                <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="e.g., Chilled/Hybrid Meals: Monday (M) Delivery" className="rounded-xl" data-testid="category-name-input" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm min-h-[60px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" placeholder="Brief description" data-testid="category-description-input" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Delivery Day *</Label>
                  <Select value={formData.delivery_day} onValueChange={(value) => setFormData({ ...formData, delivery_day: value })}>
                    <SelectTrigger className="rounded-xl" data-testid="category-delivery-day-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Monday">Monday</SelectItem>
                      <SelectItem value="Wednesday">Wednesday</SelectItem>
                      <SelectItem value="Both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Meal Type *</Label>
                  <Select value={formData.meal_type} onValueChange={(value) => setFormData({ ...formData, meal_type: value })}>
                    <SelectTrigger className="rounded-xl" data-testid="category-meal-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Chilled/Hybrid">Chilled/Hybrid</SelectItem>
                      <SelectItem value="HTE Fixed">HTE Fixed</SelectItem>
                      <SelectItem value="HTE Special">HTE Special</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Rotation Type */}
              <div className="space-y-2">
                <Label>Rotation Type</Label>
                <Select value={formData.rotation_type} onValueChange={(value) => setFormData({ ...formData, rotation_type: value })}>
                  <SelectTrigger className="rounded-xl" data-testid="category-rotation-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fixed">
                      <div className="flex items-center gap-2"><Lock className="w-3 h-3" /><span>Fixed — Same products every week</span></div>
                    </SelectItem>
                    <SelectItem value="Rotating">
                      <div className="flex items-center gap-2"><RotateCcw className="w-3 h-3" /><span>Rotating — Products vary by Menu Week</span></div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {formData.rotation_type === 'Rotating' && (
                  <p className="text-xs text-amber-600">Products in this category will be assigned to Week 1, 2, or 3 of the menu cycle</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Sort Order</Label>
                <Input type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} className="rounded-xl" data-testid="category-sort-order-input" />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="is_active">Active</Label>
                <Switch id="is_active" checked={formData.is_active} onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} data-testid="category-active-switch" />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Cancel</Button>
                <Button type="submit" disabled={submitting} className="rounded-full bg-green-600 hover:bg-green-700" data-testid="save-category-button">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : editingCategory ? 'Update Category' : 'Create Category'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminCategories;
